import {
  getRedirectResult,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';

import { auth } from './firebase';

export const GOOGLE_REDIRECT_PENDING_KEY = 'dg.googleRedirectPending';

const POPUP_REDIRECT_FALLBACK_CODES = new Set([
  'auth/popup-blocked',
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/operation-not-supported-in-this-environment',
]);

type GoogleSignInResult = 'popup' | 'redirect';

type GoogleSignInOptions = {
  onRedirectStart?: () => void;
};

type BrowserLike = {
  navigator?: Pick<Navigator, 'userAgent' | 'maxTouchPoints'>;
};

export function hasGoogleRedirectPendingMarker(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function setGoogleRedirectPendingMarker() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(GOOGLE_REDIRECT_PENDING_KEY, '1');
  } catch {
    // Ignore storage failures; diagnostics will report marker availability.
  }
}

export function clearGoogleRedirectPendingMarker() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(GOOGLE_REDIRECT_PENDING_KEY);
  } catch {
    // Ignore storage failures; this marker is only a UX/hydration hint.
  }
}

export async function completeGoogleRedirectSignIn(): Promise<'success' | 'none'> {
  try {
    const result = await getRedirectResult(auth);
    clearGoogleRedirectPendingMarker();
    return result?.user ? 'success' : 'none';
  } catch (err) {
    clearGoogleRedirectPendingMarker();
    throw err;
  }
}

// Always prefer popup-first. Runtime evidence on iPad/iPhone Chrome
// (ios-chrome) showed signInWithRedirect returning to the app with
// onAuthStateChanged user:false and getRedirectResult result:none, so the
// previous redirect-first heuristic was broken in those environments.
// Desktop and iPadOS-desktop Safari work fine with popup; if popup is
// genuinely unsupported the existing fallback below will switch to
// signInWithRedirect on `auth/popup-blocked`,
// `auth/popup-closed-by-user`, `auth/cancelled-popup-request`, or
// `auth/operation-not-supported-in-this-environment`.
export function shouldUseRedirectForGoogleSignIn(_browser: BrowserLike = globalThis): boolean {
  return false;
}

function shouldFallbackToRedirect(err: unknown): boolean {
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code?: unknown }).code ?? '')
      : '';
  const message = err instanceof Error ? err.message : String(err);

  return (
    POPUP_REDIRECT_FALLBACK_CODES.has(code) ||
    [...POPUP_REDIRECT_FALLBACK_CODES].some((fallbackCode) => message.includes(fallbackCode))
  );
}

/** DoneGrading app auth (openid/profile/email) — not Classroom OAuth scopes. */
export async function signInWithGoogleFirebase(
  options: GoogleSignInOptions = {}
): Promise<GoogleSignInResult> {
  const provider = new GoogleAuthProvider();
  const useRedirectFirst = shouldUseRedirectForGoogleSignIn();

  if (useRedirectFirst) {
    setGoogleRedirectPendingMarker();
    options.onRedirectStart?.();
    await signInWithRedirect(auth, provider);
    return 'redirect';
  }

  try {
    await signInWithPopup(auth, provider);
  } catch (err) {
    if (!shouldFallbackToRedirect(err)) {
      throw err;
    }
    setGoogleRedirectPendingMarker();
    options.onRedirectStart?.();
    await signInWithRedirect(auth, provider);
    return 'redirect';
  }

  if (import.meta.env.DEV) {
    console.info('[DoneGrading] Firebase Google sign-in', {
      firebaseUser: !!auth.currentUser,
      uid: auth.currentUser?.uid ?? null,
    });
  }
  return 'popup';
}
